import { Upload, Search, FileText, Inbox, PlusCircle, Calendar, TrendingUp } from "lucide-react";

export const PORTAL_FEATURE_NAV = {
  UPLOAD_DOCUMENT: {
    group: null,
    sortOrder: 10,
    dashboardDesc: "Upload a new policy or claim document",
    icon: Upload,
  },
  SEARCH_DOC: {
    group: null,
    sortOrder: 20,
    dashboardDesc: "Find documents across the portal",
    icon: Search,
  },
  ARCHIVAL_POLICY_SEARCH: {
    group: null,
    sortOrder: 30,
    dashboardDesc: "Look up archived policy records",
    icon: FileText,
  },
  ARCHIVAL_CLAIM_SEARCH: {
    group: null,
    sortOrder: 35,
    dashboardDesc: "Look up archived claim records",
    icon: FileText,
  },
  // Inbox Review is temporarily hidden from nav (do not delete).
  // DOC_INDEX_HUB: {
  //   group: "Document Index Hub",
  //   sortOrder: 40,
  //   dashboardDesc: "Review new inbox documents",
  //   icon: Inbox,
  // },
  REQUEST_POOL: {
    group: "Document Index Hub",
    sortOrder: 41,
    dashboardDesc: "View and track existing Azure DevOps work items",
    icon: Inbox,
  },
  MANUAL_ASSIGNMENT: {
    group: "Document Index Hub",
    sortOrder: 42,
    dashboardDesc: "Manually create work items, tasks, and user stories in Azure DevOps",
    icon: PlusCircle,
  },
  REQUEST_LEAVE: {
    group: "Document Index Hub",
    sortOrder: 43,
    dashboardDesc: "Submit leave requests, process manager approvals, and view live status",
    icon: Calendar,
  },
  ANALYTICS: {
    group: "Document Index Hub",
    sortOrder: 44,
    dashboardDesc: "Enterprise story metrics, user performance leaderboards, and ETA/SLA compliance tracking",
    icon: TrendingUp,
  },
};

const DEFAULT_NAV_META = {
  group: null,
  sortOrder: 999,
  dashboardDesc: "",
  icon: null,
};

export function getPortalFeatureMeta(resourceCode, resourceName) {
  const configured = PORTAL_FEATURE_NAV[resourceCode] || DEFAULT_NAV_META;
  let label = resourceName?.trim() || resourceCode;
  if (
    resourceCode === "DOC_INDEX_HUB" &&
    (!resourceName ||
      resourceName.trim() === "Document Index Hub" ||
      resourceName.trim() === "DOC_INDEX_HUB")
  ) {
    label = "Inbox Review";
  }

  let navLabel = label;
  if (resourceCode === "REQUEST_LEAVE") {
    navLabel = "Availability";
  }

  return {
    navLabel,
    group: configured.group,
    sortOrder: configured.sortOrder,
    dashboardTitle: navLabel,
    dashboardDesc: configured.dashboardDesc,
    icon: configured.icon,
  };
}

function withHubFeatures(items, { isTeamMember = false } = {}) {
  const hasHub = items.some(
    (item) => item.resource_code === "DOC_INDEX_HUB" || item.group === "Document Index Hub",
  );
  if (!hasHub) return items;

  const result = [...items];

  // Request Pool (/request-pool)
  if (!result.some((item) => item.resource_path === "/request-pool" || item.resource_code === "REQUEST_POOL")) {
    result.push({
      resource_code: "REQUEST_POOL",
      resource_path: "/request-pool",
      navLabel: "Request Pool",
      dashboardTitle: "Request Pool",
      dashboardDesc: "View and track existing Azure DevOps work items",
      group: "Document Index Hub",
      sortOrder: 41,
      icon: Inbox,
      sidebarOnly: true,
    });
  }

  // 2. Manual Assignment (/manual-assignment) - excluded for team members
  if (
    !isTeamMember &&
    !result.some((item) => item.resource_path === "/manual-assignment" || item.resource_code === "MANUAL_ASSIGNMENT")
  ) {
    result.push({
      resource_code: "MANUAL_ASSIGNMENT",
      resource_path: "/manual-assignment",
      navLabel: "Manual Assignment",
      dashboardTitle: "Manual Assignment",
      dashboardDesc: "Manually create work items, tasks, and user stories in Azure DevOps",
      group: "Document Index Hub",
      sortOrder: 42,
      icon: PlusCircle,
      sidebarOnly: true,
    });
  }

  // 3. Availability (/timeoff) -> Availability in sidebar
  const existingLeave = result.find(
    (item) => item.resource_path === "/timeoff" || item.resource_path === "/request-leave" || item.resource_code === "REQUEST_LEAVE"
  );
  if (!existingLeave) {
    result.push({
      resource_code: "REQUEST_LEAVE",
      resource_path: "/timeoff",
      navLabel: "Availability",
      dashboardTitle: "Availability",
      dashboardDesc: "Submit leave requests, process manager approvals, and view live status",
      group: "Document Index Hub",
      sortOrder: 43,
      icon: Calendar,
      sidebarOnly: true,
    });
  } else {
    existingLeave.resource_path = "/timeoff";
    existingLeave.navLabel = "Availability";
  }

  return result.sort((a, b) => a.sortOrder - b.sortOrder);
}

export function buildPortalNavigation(features = [], options = {}) {
  const isTeamMember = Boolean(options?.isTeamMember);

  const mapped = features
    .filter((feature) => {
      if (PORTAL_FEATURE_NAV[feature.resource_code] === undefined) return false;
      if (
        isTeamMember &&
        (feature.resource_code === "ANALYTICS" || feature.resource_code === "MANUAL_ASSIGNMENT")
      ) {
        return false;
      }
      return true;
    })
    .map((feature) => {
      const meta = getPortalFeatureMeta(
        feature.resource_code,
        feature.resource_name,
      );

      return {
        resource_code: feature.resource_code,
        resource_path: feature.resource_path,
        navLabel: meta.navLabel,
        group: meta.group,
        sortOrder: meta.sortOrder,
        dashboardTitle: meta.dashboardTitle,
        dashboardDesc: meta.dashboardDesc,
        icon: meta.icon,
      };
    })
    .sort((a, b) => a.sortOrder - b.sortOrder);

  let enriched = withHubFeatures(mapped, { isTeamMember });

  if (isTeamMember) {
    enriched = enriched.filter(
      (item) =>
        item.resource_code !== "ANALYTICS" &&
        item.resource_code !== "MANUAL_ASSIGNMENT",
    );
  }

  const topLevel = enriched.filter((item) => !item.group);
  const groupedItems = enriched.filter((item) => item.group);
  const groupMap = new Map();

  groupedItems.forEach((item) => {
    if (!groupMap.has(item.group)) {
      groupMap.set(item.group, []);
    }

    groupMap.get(item.group).push(item);
  });

  const groups = Array.from(groupMap.entries()).map(([groupLabel, items]) => ({
    groupLabel,
    items,
  }));

  return { topLevel, groups };
}

export function buildDashboardModules(features = [], options = {}) {
  const isTeamMember = Boolean(options?.isTeamMember);
  const { topLevel, groups } = buildPortalNavigation(features, options);
  const items = topLevel.concat(...groups.map((group) => group.items));

  return items
    .filter((item) => {
      if (!item.icon || item.sidebarOnly) return false;
      if (
        isTeamMember &&
        (item.resource_code === "ANALYTICS" || item.resource_code === "MANUAL_ASSIGNMENT")
      ) {
        return false;
      }
      return true;
    })
    .map((item) => ({
      key: item.resource_code,
      title: item.dashboardTitle,
      desc: item.dashboardDesc,
      path: item.resource_path,
      icon: item.icon,
    }));
}
